import { FC, memo } from 'react'
import { getPlayerId } from '../controllers/stats'
import { useLocation } from 'wouter'
import archivePuzzleExists from '../util/archivePuzzleExists'
import ArrowIcon from './Icon/ArrowIcon'
import Button from './Button'
import calculateScore from '../util/calculateScore'
import generateSuccessMessage from '../util/generateSuccessMessage'
import NextGameCountdown from './NextGameCountdown'
import RetryIcon from './Icon/RetryIcon'
import ShareButton from './ShareButton'
import shareResult from '../util/shareResult'
import StarIcon from './Icon/StarIcon'

type Props = {
	cols: number
	isArchive: boolean
	puzzleNumber: number
	resetGame(): void
	totalTime: number
}

const Board: FC<Props> = ({ cols, isArchive, puzzleNumber, resetGame, totalTime }) => {
	const [, navigate] = useLocation()

	// Create the postgame messages
	const starCount = calculateScore(totalTime)
	const [message1, message2] = generateSuccessMessage(starCount, getPlayerId(), puzzleNumber)
	const seconds = Math.floor(totalTime % 60)
	const mins = Math.floor(totalTime / 60)
	let time = ``
	if (mins > 0) time += `${mins} minute${mins === 1 ? '' : 's'}`
	if (mins > 0 && seconds > 0) time += ' and '
	if (seconds > 0) time += `${seconds} second${seconds === 1 ? '' : 's'}`
	const message3 = `You completed that in ${time}.`

	const onShare = () => shareResult(starCount, totalTime, puzzleNumber)

	const nextPuzzle = archivePuzzleExists(puzzleNumber + 1) ? puzzleNumber + 1 : null

	return (
		<div className={`board board--cols-${cols}`}>
			{/**
			 * Looking for the letter and column components?
			 * They're all added to the board by the setupBoard() function
			 * in the game initialization useEffect in Game.tsx
			 */}
			<div className="board__postgame">
				<div className="board__message1">{message1}</div>
				<div className="board__message2">{message2}</div>
				<div className="board__stars">
					{Array.from({ length: starCount }, (_, i) => (
						<StarIcon key={i} filled />
					))}
				</div>
				<div className="board__message3">{message3}</div>
				<div className="board__message3 board__message3--short">{time}</div>
				{isArchive ? (
					<div className="board__archive-buttons">
						<div>
							<Button name="retry-puzzle" onClick={resetGame}>
								<RetryIcon />
								<span>RETRY</span>
							</Button>
							{nextPuzzle && (
								<Button
									name="next-puzzle"
									onClick={() => {
										navigate(`/archive/daily/${nextPuzzle}`, {
											// If they're rattling through the archive and they click back on the browser,
											// we want them to go back to the Archive list rather than the last puzzle
											replace: true,
										})
									}}
								>
									<span>NEXT</span>
									<ArrowIcon />
								</Button>
							)}
						</div>
						<Button
							name="back-to-archive"
							onClick={() => {
								navigate('/archive')
							}}
						>
							GO BACK TO ARCHIVE
						</Button>
					</div>
				) : (
					<>
						<ShareButton onClick={onShare} />
						<div className="board__nextgame">
							Next game:&nbsp;
							<NextGameCountdown />
						</div>
					</>
				)}
			</div>
		</div>
	)
}

export default memo(Board)
